import type { Track } from "../core/types";
import { cached } from "../lib/cache";
import { decodeEntities } from "../lib/text";

/**
 * Internet Archive как второй пул.
 *
 * Зачем: Jamendo даёт богатые метаданные, но у него своя окраска, и набор из
 * одного каталога быстро приедается. Архив добавляет другое — 76 тысяч записей
 * под Creative Commons, CORS открыт, аудио отдаётся напрямую.
 *
 * Чего у него нет: `speed`, `vocalinstrumental`, `acousticelectric` — трёх из
 * четырёх осей подбора. Остаются только теги. Поэтому ранжирование обязано
 * честно обходиться с отсутствующими полями, иначе эти треки никогда не
 * выиграют у джамендовских (см. rank.ts).
 */

const SEARCH = "https://archive.org/advancedsearch.php";
const META = "https://archive.org/metadata";

/**
 * Коллекция, а не весь архив.
 *
 * `audio_music` оказался непригоден: по тегу «ambient» там 1266 записей, из
 * них верхние почти все одного плодовитого автора, а «теги» — расщеплённое на
 * слова название («bull, of, heaven, 333, course»). Совпадать с профилем такому
 * нечем.
 *
 * `netlabels` — курируемые релизы нетлейблов под CC: 14 694 записи по тому же
 * тегу, настоящие жанровые метки и разные авторы. `opensource_audio` добавляет
 * ещё столько же примерно того же качества.
 */
const COLLECTIONS = "netlabels OR opensource_audio";

/**
 * Сколько записей разворачиваем в треки. Каждая — отдельный запрос, поэтому
 * добавке хватает четырёх, а когда архив заменяет Jamendo целиком — нужно
 * больше, иначе очередь выйдет на три трека.
 */
const ITEMS_ENRICH = 4;
const ITEMS_PRIMARY = 8;
/** Не больше двух вещей с одного альбома, иначе он займёт всю очередь. */
const PER_ITEM = 2;

interface Doc {
  identifier: string;
  title?: string;
  creator?: string | string[];
  subject?: string | string[];
  licenseurl?: string;
  downloads?: number;
}

/**
 * `subject` приходит то массивом, то одной строкой со всем подряд:
 * «miranda shvangiradze;vkrs;classical; piano; downtempo». Без разбора по
 * разделителям такой «тег» не совпадёт ни с чем, и весь второй пул был бы
 * мёртвым грузом.
 */
const asList = (v: string | string[] | undefined): string[] => {
  const raw = Array.isArray(v) ? v : v ? [v] : [];
  return raw
    .flatMap((x) => String(x).split(/[;,|]/))
    .map((x) => x.trim().toLowerCase())
    .filter((x) => x.length > 1 && x.length < 30);
};

/** «03:24» или «204.5» — приводим к секундам. */
function seconds(len: unknown): number {
  const s = String(len ?? "");
  if (s.includes(":")) {
    const parts = s.split(":").map(Number);
    return parts.reduce((acc, p) => acc * 60 + (Number.isFinite(p) ? p : 0), 0);
  }
  const n = Number(s);
  return Number.isFinite(n) ? Math.round(n) : 0;
}

/** Имя файла в название трека: без пути, расширения и номера в начале. */
function titleFrom(name: string, fallback: string): string {
  const base = name.split("/").pop() ?? name;
  const clean = base
    .replace(/\.[a-z0-9]+$/i, "")
    .replace(/^\s*\d{1,2}[\s._-]+/, "")
    .replace(/[_]+/g, " ")
    .trim();
  return clean || fallback;
}

/** Короткое имя лицензии из ссылки: .../licenses/by-nd/4.0/ → CC BY-ND 4.0 */
function licenceName(url: string | undefined): string {
  if (!url) return "CC";
  const m = url.match(/licenses\/([a-z-]+)\/([\d.]+)/i);
  if (m) return `CC ${m[1]!.toUpperCase()} ${m[2]}`;
  if (/publicdomain\/zero/.test(url)) return "CC0";
  return "CC";
}

/**
 * Порядок и страница выдачи — второй рычаг разнообразия.
 *
 * Замер 2026-09-15: `page=2` и `sort=addeddate desc` дают выдачи, НЕ
 * пересекающиеся с базовой ни одной записью. Без вращения архив отдавал одни
 * и те же верхние четырнадцать записей по тегу — то есть был таким же
 * неподвижным, как и Jamendo.
 *
 * `avg_rating desc` тоже непересекающийся, но в архиве почти ничего не
 * оценено, так что порядок там случайный по существу. Не берём.
 */
const SORTS = ["downloads desc", "addeddate desc"];
const PAGES = 3;

async function search(tags: string[], rotation: number): Promise<Doc[]> {
  const subject = tags.map((t) => `"${t}"`).join(" OR ");
  const q =
    `collection:(${COLLECTIONS}) AND licenseurl:(*creativecommons*) AND subject:(${subject})`;
  const sort = SORTS[rotation % SORTS.length]!;
  const page = (rotation % PAGES) + 1;
  const url =
    `${SEARCH}?q=${encodeURIComponent(q)}` +
    "&fl%5B%5D=identifier&fl%5B%5D=title&fl%5B%5D=creator&fl%5B%5D=subject" +
    "&fl%5B%5D=licenseurl&fl%5B%5D=downloads" +
    `&sort%5B%5D=${encodeURIComponent(sort)}&rows=14&page=${page}&output=json`;

  const r = await fetch(url);
  if (!r.ok) return [];
  const d = await r.json();
  return d?.response?.docs ?? [];
}

async function expand(doc: Doc): Promise<Track[]> {
  const r = await fetch(`${META}/${encodeURIComponent(doc.identifier)}`);
  if (!r.ok) return [];
  const m = await r.json();
  const md = m?.metadata ?? {};
  const tags = asList(md.subject ?? doc.subject);
  // Имя автора берём как есть: asList приводит к нижнему регистру, что верно
  // для тегов и неверно для имени.
  const rawCreator = md.creator ?? doc.creator;
  const artist = decodeEntities(
    (Array.isArray(rawCreator) ? rawCreator[0] : rawCreator)?.toString().trim() ?? "");
  // Все лицензии CC требуют указания автора. Если запись его не называет,
  // играть её нельзя — это условие лицензии, а не косметика.
  if (!artist) return [];
  const licence = licenceName(md.licenseurl ?? doc.licenseurl);

  const out: Track[] = [];
  const seenTitles = new Set<string>();

  for (const f of m?.files ?? []) {
    if (out.length >= PER_ITEM) break;
    const name: string = f?.name ?? "";

    // Только исходный VBR MP3. Производные («64Kbps MP3») — та же запись в
    // другом битрейте, а файлы вида `210.mp3.001` — куски длинной вещи,
    // нарезанные архивом, а не треки.
    if (f?.format !== "VBR MP3" || !/\.mp3$/i.test(name)) continue;

    const dur = seconds(f?.length);
    // Тот же коридор, что у Jamendo: часовые записи ломают радио.
    if (dur < 120 || dur > 480) continue;

    // Название берём у файла, если оно есть: там человеческое, а в имени
    // файла обычно код релиза.
    const raw = String(f?.title ?? "").trim() || titleFrom(name, md.title ?? doc.identifier);
    // Подчёркивания — след имени файла, даже когда строка пришла из поля title.
    const title = decodeEntities(raw).replace(/_+/g, " ").trim();
    const key = title.toLowerCase().replace(/\s+/g, " ");
    if (seenTitles.has(key)) continue;
    seenTitles.add(key);

    out.push({
      id: `ia:${doc.identifier}:${name}`,
      name: title,
      artist_name: artist,
      artist_id: `ia:${artist}`,
      album_name: decodeEntities(String(md.title ?? doc.title ?? "")) || undefined,
      album_image: `https://archive.org/services/img/${encodeURIComponent(doc.identifier)}`,
      audio: `https://archive.org/download/${encodeURIComponent(doc.identifier)}/${encodeURI(name)}`,
      duration: dur,
      shareurl: `https://archive.org/details/${encodeURIComponent(doc.identifier)}`,
      source: "archive",
      licence,
      // Из тегов записи собираем то же поле, по которому ранжируется Jamendo.
      musicinfo: { tags: { genres: tags } },
      stats: { listens_total: doc.downloads ?? 0 },
    });
  }
  return out;
}

const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));

async function load(tags: string[], items: number, rotation: number): Promise<Track[]> {
  let docs = await search(tags, rotation);
  // Страница за краем выдачи пуста — возвращаемся к началу, как и у Jamendo.
  if (!docs.length && rotation > 0) docs = await search(tags, 0);
  if (!docs.length) return [];

  // Разворачиваем по очереди с паузой: это чужие бесплатные мощности, и
  // пять запросов залпом им ни к чему.
  const tracks: Track[] = [];
  for (const doc of docs.slice(0, items)) {
    try {
      tracks.push(...(await expand(doc)));
    } catch { /* запись не развернулась — берём следующую */ }
    await sleep(200);
  }
  return tracks;
}

/**
 * Треки архива по тегам профиля. Один поиск плюс пять разворотов — около
 * шести запросов на подбор, поэтому результат держится 30 минут: каталог
 * архива меняется куда медленнее погоды.
 */
export async function fetchArchiveTracks(
  tags: string[],
  role: "enrich" | "primary" = "enrich",
  rotation = 0,
): Promise<Track[]> {
  if (!tags.length) return [];
  const items = role === "primary" ? ITEMS_PRIMARY : ITEMS_ENRICH;
  try {
    return await cached(`ia:${role}:${rotation}:${tags.join("+")}`, 30 * 60 * 1000,
      () => load(tags, items, rotation), { persist: false });
  } catch {
    return [];   // второй пул — не обязательный: без него просто меньше выбора
  }
}
